import * as React from "react";
import { isIosWebview } from "../../utils/browserSupport";

export const isHTMLAnchorElement = (target: EventTarget): target is HTMLAnchorElement => {
  return target instanceof HTMLAnchorElement && !!target.href;
};

function tokenize(text: string): Array<string> {
  const result: Array<string> = [];
  for (let i = 0; i < text.length; i++) {
    let char = text[i]!;
    let prevChar = i > 0 ? text[i - 1] : "";
    if (prevChar === "\\") {
      result.push(prevChar + char);
    } else if (char !== "\\") {
      result.push(char);
    }
  }
  return result;
}

type TextNodeType = "text";
const TextNodeType: TextNodeType = "text";

type LinkNodeType = "link";
const LinkNodeType: LinkNodeType = "link";

type EmailNodeType = "email";
const EmailNodeType: EmailNodeType = "email";

type NewLineNodeType = "newline";
const NewLineNodeType: NewLineNodeType = "newline";

type NodeType = TextNodeType | LinkNodeType | EmailNodeType | NewLineNodeType;

interface BaseNode {
  type: NodeType;
}

interface TextNode extends BaseNode {
  type: TextNodeType;
  value: string;
}

interface LinkNode extends BaseNode {
  type: LinkNodeType;
  text: string;
  dest: string;
}

interface EmailNode extends BaseNode {
  type: EmailNodeType;
  value: string;
}

interface NewLineNode extends BaseNode {
  type: NewLineNodeType;
}

type Node = TextNode | LinkNode | EmailNode | NewLineNode;

function parseMarkdownLink(tokens: Array<string>, start: number): { start: number, middle: number, end: number } | undefined {
  const parentheses: Array<number> = [];
  const brackets: Array<number> = [];
  let insideLinkText = true;
  let insideLinkDest = false;
  let linkDestIndex = -1;
  for (let i = start; i < tokens.length; i++) {
    let token = tokens[i]!;
    if (insideLinkText) {
      if (token === "[") {
        brackets.push(i);
      } else if (token === "]") {
        brackets.pop();
        if (brackets.length === 0) {
          insideLinkText = false;
        }
      }
    } else {
      if (token === "(") {
        if (!insideLinkDest) {
          insideLinkDest = true;
          linkDestIndex = i + 1;
        }
        parentheses.push(i);
      } else if (!insideLinkDest) {
        return;
      } else if (token === ")") {
        parentheses.pop();
        if (parentheses.length === 0) {
          return { start, middle: linkDestIndex, end: i };
        }
      }
    }
  }
  return;
}

function join(tokens: Array<string>): string {
  return tokens.map((token) => ({
    "\\[": "[",
    "\\]": "]",
    "\\(": "(",
    "\\)": ")",
  }[token] ?? token)).join("");
}

function splitTextNodeWithRegExp(node: TextNode, regexp: RegExp, cb: (match: string) => Node): Array<Node> {
  const nodes: Array<Node> = [];
  let end = 0;
  let m;
  do {
    m = regexp.exec(node.value);
    if (m) {
      if (m.index > end) {
        nodes.push({ type: TextNodeType, value: node.value.slice(end, m.index) });
      }
      let m0 = m[0]!;
      end = m.index + m0.length;
      nodes.push(cb(m0));
    }
  } while (m);
  if (end < node.value.length) {
    nodes.push({ type: TextNodeType, value: node.value.slice(end) });
  }
  return nodes;
}

const regexps = {
  url: new RegExp(/(https?:\/\/[^\s]+[^\s\.])/g),
  email: new RegExp(/([^\s]+@[^\s]+\.[^\s]+)/g),
  newline: new RegExp(/\n/g),
};

export const linkOnClick: React.MouseEventHandler<HTMLElement> = (ev) => {
  if (!isIosWebview) {
    const { href, target } = ev.target as HTMLAnchorElement;
    if (href && !target) {
      const topWindowOrigin = new URLSearchParams(window.location.search).get("origin");
      if (topWindowOrigin) {
        ev.preventDefault();
        const sameDomain = new URL(topWindowOrigin).hostname === new URL(href).hostname;
        const newTarget = sameDomain ? "_top" : "_blank";
        window.open(href, newTarget);
      }
    }
  }
};

export function ParsedMessage(args: { text: string, linkTarget?: "_blank" | "_self" | "_parent" | "_top", markdown: boolean }) {
  const { text, linkTarget, markdown } = args;
  const result: Array<Node | undefined> = [];
  const tokens = tokenize(text);
  let currentNode: Node | undefined = undefined;
  if (markdown) {
    for (let i = 0; i < tokens.length; i++) {
      let token = tokens[i]!;
      if (token === "[") {
        let link = parseMarkdownLink(tokens, i);
        if (link) {
          const { start, middle: linkDestIndex, end } = link;
          result.push(currentNode);
          currentNode = {
            type: LinkNodeType,
            text: join(tokens.slice(start + 1, linkDestIndex - 2)),
            dest: join(tokens.slice(linkDestIndex, end))
          };
          result.push(currentNode);
          currentNode = undefined;
          i = end;
        } else {
          currentNode ??= { type: TextNodeType, value: "" };
          currentNode.value += token;
        }
      } else {
        currentNode ??= { type: TextNodeType, value: "" };
        currentNode.value += token;
      }
    }
  } else {
    currentNode = { type: TextNodeType, value: text };
  }
  result.push(currentNode);
  const children = result
    .map((node) => {
      if (node?.type === "text") {
        return splitTextNodeWithRegExp(node, regexps.url, (m) => ({ type: LinkNodeType, text: m, dest: m }));
      } else if (node) {
        return [node];
      } else {
        return [];
      }
    })
    .reduce((acc, nodes) => acc.concat(nodes), [])
    .map((node) => {
      if (node?.type === "text") {
        return splitTextNodeWithRegExp(node, regexps.email, (m) => ({ type: EmailNodeType, value: m }));
      } else if (node) {
        return [node];
      } else {
        return [];
      }
    })
    .reduce((acc, nodes) => acc.concat(nodes), [])
    .map((node) => {
      if (node?.type === "text") {
        return splitTextNodeWithRegExp(node, regexps.newline, (m) => ({ type: NewLineNodeType }));
      } else if (node) {
        return [node];
      } else {
        return [];
      }
    })
    .reduce((acc, nodes) => acc.concat(nodes), []);
  return (
    <>
      {children.map((node, idx) =>
        <React.Fragment key={idx}>
          {
            node.type === TextNodeType
            && node.value
            || node.type === LinkNodeType
            && <a href={node.dest} target={linkTarget} onClick={linkOnClick}>{node.text}</a>
            || node.type === EmailNodeType
            && <span className="email">{node.value}</span>
            || node.type === NewLineNodeType
            && <br/>
            || false
          }
        </React.Fragment>
      )}
    </>
  );
}
